import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import {
  RexApiContext,
  StatisticsForEntityRequest,
  StatsWithTarget,
} from '@urbanx/agx.rex.client';
import dayjs from 'dayjs';

const createQueryKey = ({
  entityType,
  selectedAgent,
  contractDateToUse,
  timePeriod,
  fromDate,
  toDate,
}: StatisticsForEntityRequest) => [
  'GetStatisticsForEntity',
  entityType,
  selectedAgent,
  contractDateToUse,
  timePeriod,
  fromDate,
  toDate,
];

export const useInvalidateLoadRexStatsByTypeByDate = (
  filters: StatisticsForEntityRequest
) => {
  const queryClient = useQueryClient();

  const queryKey = createQueryKey(filters);

  return async () => await queryClient.invalidateQueries({ queryKey });
};

export const useLoadRexStatsByTypeByDate = (
  filters: StatisticsForEntityRequest
): {
  agencyDashboardStatistics: StatsWithTarget | undefined;
  isPending: boolean;
  isError: boolean;
} => {
  const {
    entityType,
    selectedAgent,
    contractDateToUse,
    timePeriod,
    fromDate,
    toDate,
  } = filters;

  const rexApi = useContext(RexApiContext);

  const convertedFromDate = dayjs(fromDate || '');
  const convertedToDate = dayjs(toDate || '');

  const finalFromDate = convertedFromDate.isValid()
    ? convertedFromDate.toDate()
    : undefined;
  const finalToDate = convertedToDate.isValid()
    ? convertedToDate.toDate()
    : undefined;

  const {
    data: agencyDashboardStatistics,
    isPending,
    isError,
  } = useQuery<StatsWithTarget | undefined>({
    queryKey: [
      'GetStatisticsForEntity',
      entityType,
      selectedAgent,
      contractDateToUse,
      timePeriod,
      fromDate,
      toDate,
    ],
    queryFn: () => rexApi.getStatisticsForEntity(filters),
    enabled: finalFromDate !== undefined && finalToDate !== undefined,
  });

  return {
    agencyDashboardStatistics,
    isPending,
    isError,
  };
};
